import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import styles from './styles.module.scss';

if (typeof HighchartsMore === 'function') {
  HighchartsMore(Highcharts);
}

const GAUGE_HEIGHT_PX = 130;
const GAUGE_CENTER = ['50%', '75%'] as const;
const GAUGE_SIZE = '140%';
const GAUGE_START_ANGLE = -90;
const GAUGE_END_ANGLE = 90;
const GAUGE_PLOT_BAND_THICKNESS = 20;
const GAUGE_DIAL_RADIUS = '75%';
const GAUGE_DIAL_COLOR = '#505050';
const GAUGE_DIAL_BASE_WIDTH = 10;
const GAUGE_DIAL_BASE_LENGTH = '0%';
const GAUGE_DIAL_REAR_LENGTH = '0%';
const GAUGE_DIAL_TOP_WIDTH = 1;
const GAUGE_PIVOT_RADIUS = 5;

const formatElasticityValue = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '—';
  }
  let decimals = 2;
  if (value !== 0 && Math.abs(value) < 0.05) {
    decimals = 3;
  }
  // Evita "+0,00" quando o modelo devolve elasticidade > 0 mas < 0,005 (2 casas zeram).
  if (value !== 0 && Number(Math.abs(value).toFixed(2)) === 0) {
    decimals = 4;
  }
  const formatted = value.toFixed(decimals).replace('.', ',');
  return value >= 0 ? `+${formatted}` : formatted;
};

export const ElasticityGaugeChart = () => {
  const { elasticityResponse } = useElasticidadeCruzadaStore();

  if (!elasticityResponse) return null;

  const { elasticity, classification, gaugeConfig } = elasticityResponse;
  const gaugePointerValue = gaugeConfig.pointerValue;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'gauge',
      backgroundColor: 'transparent',
      height: GAUGE_HEIGHT_PX,
      spacing: [0, 0, 0, 0],
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    pane: {
      startAngle: GAUGE_START_ANGLE,
      endAngle: GAUGE_END_ANGLE,
      background: undefined,
      center: GAUGE_CENTER as unknown as string[],
      size: GAUGE_SIZE,
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      min: gaugeConfig.min,
      max: gaugeConfig.max,
      tickLength: 0,
      tickWidth: 0,
      minorTickInterval: undefined,
      lineWidth: 0,
      labels: {
        enabled: false,
      },
      plotBands: gaugeConfig.plotBands.map(band => ({
        from: band.from,
        to: band.to,
        color: band.color,
        thickness: GAUGE_PLOT_BAND_THICKNESS,
      })),
    },
    plotOptions: {
      gauge: {
        wrap: false,
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: [
      {
        type: 'gauge',
        name: 'Elasticidade',
        data: [gaugePointerValue],
        dial: {
          radius: GAUGE_DIAL_RADIUS,
          backgroundColor: GAUGE_DIAL_COLOR,
          baseWidth: GAUGE_DIAL_BASE_WIDTH,
          baseLength: GAUGE_DIAL_BASE_LENGTH,
          rearLength: GAUGE_DIAL_REAR_LENGTH,
          topWidth: GAUGE_DIAL_TOP_WIDTH,
        },
        pivot: {
          backgroundColor: GAUGE_DIAL_COLOR,
          radius: GAUGE_PIVOT_RADIUS,
        },
      },
    ],
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Elasticidade média do produto</h3>
      <div className={styles.chartContainer}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>
      <div className={styles.valueDisplay}>{formatElasticityValue(elasticity)}</div>
      <div className={styles.infoWrapper}>
        <p className={styles.classificationText}>Esse produto é {classification}</p>
        <p className={styles.description}>Quando comparado aos produtos concorrentes</p>
      </div>
    </div>
  );
};
